import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Box, Button, Callout, Checkbox, Flex, Grid, Link, Text, TextField } from '@radix-ui/themes'
import { useTRPC } from '~/utils/trpc'
import { useAuth } from '~/auth/AuthProvider'
import { todayIso, nowTime, formatTimestamp, isoToLocalDateTime } from '~/utils/date'
import {
  listConfirmedIndexes,
  listInstallationCaptures,
  type ConfirmedIndex,
  type InstallationCapture
} from '~/features/installations/service'
import {
  buildDataSourceInput,
  buildDeviceDescription,
  dataSourceReuseWarning,
  dataSourceStatusWarning,
  initialDataSourceValues,
  isSupportedDataSourceMaker,
  validateDataSourceValues,
  type MeterDataSourceFieldValues
} from '~/features/installations/dataSource'
import { MeterDataSourceFields } from './MeterDataSourceFields'

const readingLabel = (entry: ConfirmedIndex): string =>
  entry.round === null ? 'At installation' : `Verification round ${entry.round}`

const ConfirmedIndexNote = ({
  entries,
  selected,
  onPick
}: {
  entries: ConfirmedIndex[]
  selected: ConfirmedIndex | null
  onPick: (entry: ConfirmedIndex) => void
}) => {
  if (!selected) return null

  return (
    <Flex direction="column" gap="1" mt="1">
      <Text size="1" color="gray">
        {readingLabel(selected)} ·{' '}
        {selected.source === 'typed' ? 'Typed by the installer' : 'Read from the dial photo'}{' '}
        {formatTimestamp(selected.confirmedAt)}
      </Text>
      {entries
        .filter((entry) => entry.confirmedAt !== selected.confirmedAt)
        .map((entry) => (
          <Link key={entry.confirmedAt} size="1" href="#" onClick={() => onPick(entry)}>
            Use {readingLabel(entry).toLowerCase()} ({entry.value} m³)
          </Link>
        ))}
    </Flex>
  )
}

const DialPhoto = ({ capture }: { capture: InstallationCapture }) => {
  const [errored, setErrored] = useState(false)

  if (errored || !capture.url) {
    return (
      <Text size="1" color="gray">
        The dial photo failed to load — read the index off the meter instead.
      </Text>
    )
  }

  return (
    <Flex direction="column" gap="1">
      <a
        href={capture.url}
        target="_blank"
        rel="noreferrer"
        className="block overflow-hidden rounded-[var(--radius-2)] border border-[color:var(--gray-5)] bg-[var(--gray-2)]"
      >
        <img
          src={capture.url}
          alt="Meter dial"
          className="max-h-56 w-full object-contain"
          onError={() => setErrored(true)}
        />
      </a>
      <Text size="1" color="gray">
        Shot in the app {formatTimestamp(capture.receivedAt)} · click to open full size
      </Text>
    </Flex>
  )
}

interface InstallStationFormProps {
  installationId: number
  orgName: string | null
  defaultLoggerMaker: string | null
  defaultSerialNumber: string | null
  defaultPulseWeight: number | null
  onClose: () => void
}

export const InstallStationForm = ({
  installationId,
  orgName,
  defaultLoggerMaker,
  defaultSerialNumber,
  defaultPulseWeight,
  onClose
}: InstallStationFormProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { supabaseClient } = useAuth()

  const [createIndex, setCreateIndex] = useState(true)
  const { data: confirmedIndexes } = useQuery({
    queryKey: ['confirmed-indexes', installationId],
    queryFn: () => listConfirmedIndexes(supabaseClient, installationId)
  })

  const [pickedAt, setPickedAt] = useState<string | null>(null)
  const confirmedIndex =
    confirmedIndexes?.find((entry) => entry.confirmedAt === pickedAt) ??
    confirmedIndexes?.find((entry) => entry.round === null) ??
    confirmedIndexes?.[0] ??
    null

  // Derived, not synced in an effect: this query resolves after the form mounts, and a `useState`
  // seeded from it would keep the empty string it was born with.
  const [typedIndex, setTypedIndex] = useState<string | null>(null)
  const indexValue = typedIndex ?? (confirmedIndex ? String(confirmedIndex.value) : '')

  /** The dial the installer shot, so the operator confirms the index against it rather than blind. */
  const { data: captures } = useQuery({
    queryKey: ['installation-captures', installationId],
    queryFn: () => listInstallationCaptures(supabaseClient, installationId)
  })
  const dialPhoto = captures?.find((capture) => capture.kind === 'meter_index') ?? null

  // The dial was read when it was photographed; a confirmation without a photo is manual entry.
  const readAt = dialPhoto?.receivedAt ?? confirmedIndex?.confirmedAt ?? null
  const [typedDate, setTypedDate] = useState<string | null>(null)
  const [typedTime, setTypedTime] = useState<string | null>(null)
  const indexDate = typedDate ?? (readAt ? isoToLocalDateTime(readAt).date : todayIso())
  const indexTime = typedTime ?? (readAt ? isoToLocalDateTime(readAt).time : nowTime())

  const [createDataSource, setCreateDataSource] = useState(isSupportedDataSourceMaker(defaultLoggerMaker))
  const [dataSource, setDataSource] = useState<MeterDataSourceFieldValues>(() =>
    initialDataSourceValues(
      defaultLoggerMaker,
      defaultSerialNumber,
      defaultPulseWeight,
      buildDeviceDescription(orgName, installationId)
    )
  )

  const [warning, setWarning] = useState<string | null>(null)

  const mutation = useMutation(
    trpc.ops.stationInstallations.install.mutationOptions({
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.get.queryKey({ installationId }) })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
        const dsWarning =
          dataSourceStatusWarning(result.dataSourceStatus, dataSource) ?? dataSourceReuseWarning(result)
        if (dsWarning) {
          setWarning(dsWarning)
        } else {
          onClose()
        }
      }
    })
  )

  const validationError = (): string | null => {
    if (createIndex) {
      if (!(Number(indexValue) >= 0) || indexValue.trim() === '') return 'Index value must be a positive number'
      if (!/^\d{4}-\d{2}-\d{2}$/.test(indexDate)) return 'Index date is invalid'
      if (!/^\d{2}:\d{2}$/.test(indexTime)) return 'Index time is invalid'
    }
    if (createDataSource) return validateDataSourceValues(dataSource)
    return null
  }

  const error = validationError()

  const handleSubmit = () => {
    if (error) return
    const indexReading = createIndex
      ? { value: Number(indexValue), readingAt: new Date(`${indexDate}T${indexTime}:00`).toISOString() }
      : null
    mutation.mutate({
      installationId,
      index: indexReading,
      // Reuse the same reading for the Shayp installation so it isn't asked for twice.
      dataSource: createDataSource ? buildDataSourceInput(dataSource, indexReading ?? undefined) : null
    })
  }

  if (warning) {
    return (
      <Flex direction="column" gap="3" mt="4">
        <Callout.Root color="green">
          <Callout.Text>Station marked installed.</Callout.Text>
        </Callout.Root>
        <Callout.Root color="amber">
          <Callout.Text>{warning}</Callout.Text>
        </Callout.Root>
        <Flex justify="end">
          <Button variant="solid" onClick={onClose}>
            Done
          </Button>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="4" mt="4">
      <Box>
        <label className="flex items-center gap-2 mb-3">
          <Checkbox checked={createIndex} onCheckedChange={(v) => setCreateIndex(v === true)} />
          <Text size="2" weight="medium">
            Record initial index
          </Text>
        </label>
        {createIndex ? (
          <Flex direction="column" gap="3">
            {dialPhoto ? <DialPhoto capture={dialPhoto} /> : null}
            <Grid columns="3" gap="3">
              <Box>
                <Text as="label" size="1" color="gray" className="block mb-1">
                  Index value (m³)
                </Text>
                <TextField.Root type="number" value={indexValue} onChange={(e) => setTypedIndex(e.target.value)} />
                <ConfirmedIndexNote
                  entries={confirmedIndexes ?? []}
                  selected={confirmedIndex}
                  onPick={(entry) => {
                    setPickedAt(entry.confirmedAt)
                    setTypedIndex(String(entry.value))
                  }}
                />
              </Box>
              <Box>
                <Text as="label" size="1" color="gray" className="block mb-1">
                  Date
                </Text>
                <TextField.Root
                  type="date"
                  value={indexDate}
                  max={todayIso()}
                  onChange={(e) => setTypedDate(e.target.value)}
                />
              </Box>
              <Box>
                <Text as="label" size="1" color="gray" className="block mb-1">
                  Time
                </Text>
                <TextField.Root type="time" value={indexTime} onChange={(e) => setTypedTime(e.target.value)} />
              </Box>
            </Grid>
          </Flex>
        ) : null}
      </Box>

      <Box className="border-t border-t-[color:var(--gray-6)] pt-3">
        <label className="flex items-center gap-2 mb-3">
          <Checkbox checked={createDataSource} onCheckedChange={(v) => setCreateDataSource(v === true)} />
          <Text size="2" weight="medium">
            Create data source
          </Text>
        </label>
        {createDataSource ? <MeterDataSourceFields values={dataSource} onChange={setDataSource} /> : null}
      </Box>

      {mutation.error ? (
        <Text color="red" size="2">
          {mutation.error.message}
        </Text>
      ) : null}

      <Flex gap="2" justify="end">
        <Button variant="soft" color="gray" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="solid"
          disabled={!!error || mutation.isPending}
          loading={mutation.isPending}
          onClick={handleSubmit}
        >
          Mark installed
        </Button>
      </Flex>
    </Flex>
  )
}
